var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
/*
 * THE COMPOSER — what the AI rail IS, not a button that leads to it.
 *
 * Opening the rail lands here, in an empty conversation ready to take a sentence. The previous
 * shape offered a "New conversation" button as one of two doors, which made the overwhelmingly
 * common intent cost a click and spent most of a 360px rail on a list of the rarer one. Starting is
 * now the surface itself; the list moved behind the back arrow, where the rarer intent belongs.
 *
 * WHAT THE BOTTOM DOCK IS FOR. Everything that shapes the message sits with the message: which
 * model answers it, what is attached to it, and how it is sent. Those are per-conversation choices
 * and they are deliberately NOT persisted — a model picked for one question should not silently
 * become the default for every question after it. Durable preferences live in the AI dashboard,
 * which is the only place that claims to remember anything.
 *
 * THE ONE BUTTON. Bottom-right is a single control that means "go": it listens when there is
 * nothing to send and sends when there is. Two buttons where one always makes sense is a choice the
 * user has to make before they can act, and the empty composer already answers it.
 */
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state, query } from 'lit/decorators.js';
import { t } from '../i18n';
import { LocaleController } from '../i18n/LocaleController';
import { greetingSeed } from './greeting';
import { readDraft, saveDraft, NEW_CHAT_DRAFT } from './session-memory';
import { greetingView, greetingCss } from './greeting-view';
import { ACCEPT_ATTR, stagePicked } from './attachments';
import { chipStyles, renderChips } from './attachment-chips';
import { REALTIME_MODELS, TEXT_MODELS, IMAGE_MODELS, catalogEvents } from './model-catalog';
import { iconButton, backButton } from '../elements/shared';
import '../elements/vitesse-icon';
import './tools-menu';
import '../elements/vitesse-button';
let VitesseAiComposer = class VitesseAiComposer extends LitElement {
    constructor() {
        super(...arguments);
        /** Which app this is, for per-surface storage. Absent ⇒ the draft is not kept. */
        this.surface = '';
        /** The person, for the greeting. Absent is fine — the rotation has an anonymous form of every line. */
        this.userName = '';
        /** The org's configured models, shown as the pickers' placeholders so "inherit" is legible rather
         *  than blank. */
        /** The org's specialists, from the handshake roster. Empty hides the picker. */
        this.specialists = [];
        this.defaultChatModel = '';
        this.defaultVoiceModel = '';
        /** The org's IMAGE default, for the third row in the model menu. Empty on a surface with no
         *  image capability, which is what makes that row disappear rather than render blank. */
        this.defaultImageModel = '';
        this.text = '';
        this.chatModel = '';
        /** `medium` matches what every turn asked for when it was hardcoded, so making it choosable
         *  changes who decides rather than what happens by default. */
        this.effort = 'medium';
        this.expertId = '';
        this.voiceModel = '';
        this.files = [];
        /** Named for the user, e.g. "logo.svg — not a supported type". Cleared on the next pick. */
        this.rejected = '';
        /** Fixed for the life of this mount: a greeting that reshuffled per keystroke would be motion
         *  nobody asked for. */
        this.seed = greetingSeed();
        // eslint-disable-next-line @typescript-eslint/no-unused-vars -- re-renders on locale change
        this.locale = new LocaleController(this);
        /**
         * Re-render when the live catalog replaces the baked one.
         *
         * The overlay is in place — same array identity — so nothing here would otherwise notice, and the
         * pickers would sit on the baked rows with no prices for the life of the panel.
         */
        this.onCatalog = () => this.requestUpdate();
        this.onInput = (e) => {
            const el = e.target;
            this.text = el.value;
            // Kept across leaving the surface. A new chat has no conversation to key on, so it uses the
            // reserved one — see NEW_CHAT_DRAFT.
            if (this.surface)
                saveDraft(this.surface, NEW_CHAT_DRAFT, this.text);
            this.grow(el);
        };
        this.onKey = (e) => {
            // Enter sends; Shift+Enter is a newline. IME composition is left alone — during composition
            // Enter commits the candidate, and stealing it would send half a word in Japanese or Chinese.
            if (e.key === 'Enter' && !e.shiftKey && !e.isComposing) {
                e.preventDefault();
                this.submit();
            }
        };
        this.onPick = (e) => {
            const el = e.target;
            ({ files: this.files, rejected: this.rejected } = stagePicked(this.files, [...(el.files ?? [])], t));
            el.value = ''; // so the same file can be re-picked after being removed
        };
    }
    /**
     * Choose who answers, and take their settings with them.
     *
     * The same rule the in-thread menu follows, and it has to be the same or the
     * two places that pick a specialist would configure it differently: a
     * specialist was given a model and an effort that suit its job, so picking one
     * applies both. Leaving the model alone would run a specialist tuned for a
     * big-context model on whatever the last thread happened to use.
     *
     * Still editable afterwards — the rows beneath keep working, because this is a
     * starting point rather than a lock.
     */
    pickSpecialist(id) {
        this.expertId = id;
        const spec = this.specialists.find((e) => e.id === id);
        if (!spec)
            return;
        if (spec.model)
            this.chatModel = spec.model;
        if (spec.effort)
            this.effort = spec.effort;
    }
    connectedCallback() {
        super.connectedCallback();
        catalogEvents.addEventListener('change', this.onCatalog);
        /*
         * WHAT WAS BEING TYPED, STILL THERE.
         *
         * Restored on mount rather than on a property change: this surface is created and destroyed as
         * the rail moves between the list and a new chat, so "mounted" IS the moment somebody came
         * back to it. Only when nothing has been typed since, so a restore cannot overwrite live input.
         */
        if (!this.text && this.surface)
            this.text = readDraft(this.surface, NEW_CHAT_DRAFT);
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        catalogEvents.removeEventListener('change', this.onCatalog);
    }
    static { this.styles = [
        iconButton,
        chipStyles,
        backButton,
        css `
      :host {
        display: flex;
        flex-direction: column;
        min-height: 0;
        flex: 1;
      }

      /* ── the top rail: back, and nothing else ─────────────────────────────
       One control, because there is one place to go from here. */
      /* 52px — the base pages' header height, so the rail's three surfaces and every page in the app
       put their back control on the same line. */
      .top {
        height: 52px;
        box-sizing: border-box;
        display: flex;
        align-items: center;
        padding: 0 12px;
        flex-shrink: 0;
      }

      /* ── the greeting: the majority of the panel, deliberately ────────────
       Empty space above a composer is not waste; it is what makes the composer the subject. */
      .welcome {
        flex: 1 1 auto;
        min-height: 0;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 10px;
        padding: 12px 20px 20px;
        text-align: center;
      }
      .hi {
        margin: 0;
        font-size: 21px;
        font-weight: 600;
        line-height: 1.25;
        /* The app's own gradient on the app's own greeting — the same treatment the home hero uses,
         so the two read as one product. Clipped to the text box, not the row. */
        background: linear-gradient(135deg, #1652c9, #2491db 55%, #6fb2f4);
        -webkit-background-clip: text;
        background-clip: text;
        -webkit-text-fill-color: transparent;
        color: transparent;
        width: fit-content;
        max-width: 100%;
      }
      .sub {
        margin: 0;
        font-size: 13px;
        line-height: 1.5;
        color: var(--text-secondary);
        max-width: 30ch;
      }

      /* ── the dock ─────────────────────────────────────────────────────── */
      .dockrow {
        display: flex;
        align-items: flex-end;
        gap: 8px;
        flex-shrink: 0;
        margin: 0 12px 12px;
      }
      .dock {
        flex: 1 1 auto;
        min-width: 0;
        border: 1px solid var(--border-color);
        border-radius: 14px;
        background: var(--bg-secondary);
        transition: border-color 0.18s ease;
      }
      .dock:focus-within {
        border-color: var(--blue);
      }

      .chips {
        padding: 9px 10px 0;
      }
      .reject {
        padding: 7px 12px 0;
        color: var(--red);
      }

      textarea {
        display: block;
        width: 100%;
        box-sizing: border-box;
        /* Grows with the message up to a ceiling, then scrolls. A fixed single line hides what you
         wrote; an unbounded one eats the panel it sits in. */
        min-height: 44px;
        max-height: 168px;
        resize: none;
        border: 0;
        background: transparent;
        padding: 12px;
        color: var(--text-primary);
        font: inherit;
        font-size: 14px;
        line-height: 1.5;
        outline: none;
        scrollbar-width: thin;
        scrollbar-color: var(--scrollbar-thumb) transparent;
      }
      textarea::placeholder {
        color: var(--text-tertiary);
      }

      /* The "+" menu and the primary button stacked on the right, OUTSIDE the box — the same
         column the live thread's composer has. */
      .rail {
        display: flex;
        flex-direction: column;
        align-items: flex-end;
        justify-content: flex-end;
        gap: 6px;
        flex-shrink: 0;
        padding-bottom: 2px;
      }
      input[type='file'] {
        display: none;
      }

    `,
    ]; }
    render() {
        const sending = this.text.trim().length > 0;
        return html `
      <div class="top">
        <button
          class="back-btn"
          @click=${() => this.onBack?.()}
          @mouseenter=${() => this.onBackHover?.()}
          @focus=${() => this.onBackHover?.()}
          title=${t('ai.hubTitle')}
        >
          <vitesse-icon name="chevron-left" size="18"></vitesse-icon>${t('ai.backToConversations')}
        </button>
      </div>

      ${greetingView(this.userName, this.seed)}

      <!-- The box holds only what is being written (chips + text). The "+" menu and the primary
           button sit BESIDE it, outside its border — the same shape as the live thread's composer,
           so the controls read as acting on the box rather than as part of what is in it. -->
      <div class="dockrow">
      <div class="dock">
        ${renderChips(this.files, this.rejected, (id) => this.drop(id))}
        <textarea
          .value=${this.text}
          rows="1"
          placeholder=${t('ai.composerPlaceholder')}
          aria-label=${t('ai.composerPlaceholder')}
          @input=${this.onInput}
          @keydown=${this.onKey}
        ></textarea>
      </div>

        <div class="rail">
          <!-- ONE control for everything that shapes the message: what is attached, and which model
               answers. Three controls could not fit a rail; a menu gives each row its full label. -->
          <vitesse-ai-tools
            .textModels=${TEXT_MODELS}
            .voiceModels=${REALTIME_MODELS}
            .imageModels=${this.defaultImageModel ? IMAGE_MODELS : []}
            .imageDefault=${this.defaultImageModel}
            .textValue=${this.chatModel}
            .voiceValue=${this.voiceModel}
            .textDefault=${this.defaultChatModel}
            .voiceDefault=${this.defaultVoiceModel}
            .onAttach=${() => this.picker?.click()}
            .onTextModel=${(v) => (this.chatModel = v)}
            .effort=${this.effort}
            .onEffort=${(v) => (this.effort = v)}
            .specialists=${this.specialists}
            .specialist=${this.expertId}
            .onSpecialist=${(id) => this.pickSpecialist(id)}
            .onVoiceModel=${(v) => (this.voiceModel = v)}
          ></vitesse-ai-tools>
          <input type="file" multiple accept=${ACCEPT_ATTR} @change=${this.onPick} />

          <!-- The SAME primary control as the active chat (chat.ts): a large purple Talk when the box
               is empty — talk is what you do with an empty box — and a large blue Send once there is
               text. The two chat panels must not present two different buttons for the one action
               everybody presses; this is that button, verbatim, so the empty composer and the live
               thread are the same surface. -->
          ${sending
            ? html `<vitesse-button size="lg" icon="send" title=${t('ai.send')} @click=${() => this.submit()}>${t('ai.send')}</vitesse-button>`
            : html `<vitesse-button size="lg" icon="mic-signal" title=${t('ai.startVoice')} @click=${() => this.startVoice()}
                  >${t('ai.talk')}</vitesse-button
                >`}
        </div>
      </div>
    `;
    }
    /** Height follows content: reset first, because scrollHeight never shrinks below the height
     *  already set — measuring without the reset makes a textarea that only ever grows. */
    grow(el) {
        el.style.height = 'auto';
        el.style.height = `${el.scrollHeight}px`;
    }
    drop(id) {
        this.files = this.files.filter((f) => f.id !== id);
    }
    submit() {
        const text = this.text.trim();
        if (!text)
            return;
        this.onSubmit?.({
            text,
            chatModel: this.chatModel,
            effort: this.effort,
            expertId: this.expertId,
            voiceModel: this.voiceModel,
            files: this.files,
        });
        this.text = '';
        if (this.surface)
            saveDraft(this.surface, NEW_CHAT_DRAFT, ''); // it became a message
        this.files = [];
        this.rejected = '';
        if (this.area) {
            this.area.value = '';
            this.grow(this.area);
        }
    }
    startVoice() {
        // The specialist rides into a voice session too: it is the same conversation
        // and the same question of who is answering, just spoken.
        this.onStartVoice?.({
            chatModel: this.chatModel,
            effort: this.effort,
            expertId: this.expertId,
            voiceModel: this.voiceModel,
            files: this.files,
        });
    }
};
__decorate([
    property()
], VitesseAiComposer.prototype, "surface", void 0);
__decorate([
    property()
], VitesseAiComposer.prototype, "userName", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiComposer.prototype, "specialists", void 0);
__decorate([
    property()
], VitesseAiComposer.prototype, "defaultChatModel", void 0);
__decorate([
    property()
], VitesseAiComposer.prototype, "defaultVoiceModel", void 0);
__decorate([
    property()
], VitesseAiComposer.prototype, "defaultImageModel", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiComposer.prototype, "onSubmit", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiComposer.prototype, "onStartVoice", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiComposer.prototype, "onBack", void 0);
__decorate([
    property({ attribute: false })
], VitesseAiComposer.prototype, "onBackHover", void 0);
__decorate([
    state()
], VitesseAiComposer.prototype, "text", void 0);
__decorate([
    state()
], VitesseAiComposer.prototype, "chatModel", void 0);
__decorate([
    state()
], VitesseAiComposer.prototype, "effort", void 0);
__decorate([
    state()
], VitesseAiComposer.prototype, "expertId", void 0);
__decorate([
    state()
], VitesseAiComposer.prototype, "voiceModel", void 0);
__decorate([
    state()
], VitesseAiComposer.prototype, "files", void 0);
__decorate([
    state()
], VitesseAiComposer.prototype, "rejected", void 0);
__decorate([
    query('textarea')
], VitesseAiComposer.prototype, "area", void 0);
__decorate([
    query('input[type=file]')
], VitesseAiComposer.prototype, "picker", void 0);
VitesseAiComposer = __decorate([
    customElement('vitesse-ai-composer')
], VitesseAiComposer);
export { VitesseAiComposer };
//# sourceMappingURL=composer.js.map